import { Control, Controller, FieldErrors, UseFormWatch } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { Checkbox, CheckPicker, SelectPicker, Tooltip, Whisper } from 'rsuite';
import { FilterSeparator, SelectPill } from '../../../../../../../components';
import { useDebounce } from '../../../../../../../hooks';
import { useSearchParams } from '../../../../../../../hooks/useSearchParams';
import { optimizationPricesSelector } from '../../../../../../../reducers/OtimizacaoPrecos';
import { setSearch } from '../../../../../../../reducers/OtimizacaoPrecos/optimizationPrices';
import { useFilterSection } from '../../../../hooks/useFilterSection';
import { FormOptimizationPrices } from '../../../../types/schema.form';
import styles from './FilterSection.module.scss';

interface FilterSectionProps {
  control: Control<FormOptimizationPrices>;
  watch: UseFormWatch<FormOptimizationPrices>;
  errors: FieldErrors<FormOptimizationPrices>;
}

export const FilterSection = ({ control, errors, watch }: FilterSectionProps) => {
  const debounce = useDebounce(500);
  const dispatch = useDispatch();
  const { listStores, handleSelectedFilters, handleSetSelectedFilterCategoryLevel, states } = useFilterSection();
  const { isListStoresLoading, indeterminateStores, isCheckedAllStores, isListCategoriesLoading, canUpdateFilters } = states;

  const { deleteParam, setParam } = useSearchParams();

  const { filters } = useSelector(optimizationPricesSelector);
  const { selectedFilters, list } = filters;

  const controlStoreSelected = watch('filters.controlStoreId') as string;
  const ignoreList = list.categoryLevel.filter(item => item.value !== selectedFilters.categoryLevel.value).map(item => item.value);
  const controlStoreList = list.stores.filter(item => !selectedFilters.storesId.includes(item.store_id));
  const showCategoriesFilterMessageError = errors.filters?.categories?.message;
  const showCategoriesFilterTip = selectedFilters.categoriesId?.length === 0 && 'Obrigatório, selecione uma categoria';

  const showStoreFilterMessageError = errors.filters?.storesId?.message;
  const showStoreFilterTip = selectedFilters.storesId.length === 0 && 'Obrigatório, selecione ao menos uma loja a otimizar';
  return (
    <section className={styles.container}>
      <div className={styles.title}>
        <span>Defina onde otimizar os preços</span>
        <p>Escolha a categoria e indique em quais lojas os preços serão otimizados</p>
      </div>
      <div className={styles.content}>
        <div className={styles['filter-selection-wrapper']}>
          <span>Categorização</span>
          <SelectPill
            id="select-picker-pill"
            placeholder="Selecione uma opção"
            type="select"
            name="category"
            filter={false}
            searchable={false}
            cleanable={false}
            value={selectedFilters.categoryLevel.value}
            data={list.categoryLevel}
            defaultValue={list.categoryLevel[0]?.label}
            onSelect={(v, item: any) => {
              handleSetSelectedFilterCategoryLevel(item);
            }}
            renderValue={(v, item: any) => {
              return (
                <span>
                  <strong>{item?.value?.toString()?.padStart(2, '0')}</strong> {item?.name}
                </span>
              );
            }}
            disabledItemValues={ignoreList}
          />

          <FilterSeparator style={{ height: '20px', width: '20px' }} />

          <Controller
            control={control}
            name="filters.categories"
            render={({ field }) => (
              <>
                <SelectPicker
                  id="select-picker-pill"
                  style={{ maxWidth: '300px' }}
                  cleanable
                  searchable
                  disabled={!canUpdateFilters}
                  placeholder={'Selecione a categoria'}
                  type="select"
                  name="categories"
                  filter={false}
                  value={selectedFilters.categoriesId?.join(',')}
                  data={list.categories}
                  onClose={() => {
                    listStores();
                  }}
                  onSelect={value => {
                    const newValue = typeof value === 'string' ? [value] : value;
                    handleSelectedFilters({ type: 'categoriesId', value: newValue });
                    field.onChange(newValue);
                    setParam('categoriesId', newValue?.join(','));
                  }}
                  onClean={() => {
                    handleSelectedFilters({ type: 'categoriesId', value: [] });
                    field.onChange([]);
                    deleteParam('categoriesId');
                  }}
                  locale={{
                    noResultsText: isListCategoriesLoading ? 'Carregando categorias...' : 'Nenhum item encontrado',
                    searchPlaceholder: 'Pesquisar categorias',
                  }}
                  onSearch={search => {
                    debounce(() => {
                      dispatch(setSearch({ type: 'categories', value: search }));
                    });
                  }}
                  // disabledItemValues={[...optimizedCategoriesId]}
                  renderMenuItem={(label, item: any) => {
                    const maxCategories = 2;
                    const firstTreeCategories = item?.stores?.filter((_store: string, index: number) => index < maxCategories);
                    const restCategories = item?.stores?.filter((_store: string, index: number) => index >= maxCategories);
                    const allStores = item?.stores?.join(', ');

                    return allStores ? (
                      <Whisper trigger="hover" placement="right" speaker={<Tooltip>{allStores}</Tooltip>}>
                        <div className={styles['render-menu-item']}>
                          <span>{label}</span>
                          {firstTreeCategories?.map((store: string) => (
                            <span key={store} className={styles['highlight-optimized']}>
                              {store}
                            </span>
                          ))}
                          {restCategories?.length > 0 && (
                            <span className={styles['highlight-optimized']}>
                              + {restCategories.length} {restCategories.length > 1 ? 'OUTRAS' : 'OUTRA'}
                            </span>
                          )}
                        </div>
                      </Whisper>
                    ) : (
                      <span>{label}</span>
                    );
                  }}
                />
                {showCategoriesFilterMessageError ? <p className={styles.error}>{showCategoriesFilterMessageError} </p> : null}
                {!showCategoriesFilterMessageError && showCategoriesFilterTip ? <p className={styles.tip}>{showCategoriesFilterTip}</p> : null}
              </>
            )}
          />
        </div>

        <div className={styles['filter-selection-wrapper']}>
          <span>Loja a otimizar</span>
          <Controller
            control={control}
            name="filters.storesId"
            render={({ field }) => (
              <>
                <CheckPicker
                  id="select-picker-pill"
                  style={{ maxWidth: '300px' }}
                  cleanable
                  searchable
                  placeholder={'Selecione as lojas'}
                  type="select"
                  name="loja"
                  filter={false}
                  value={selectedFilters.storesId}
                  data={list.stores}
                  disabledItemValues={[controlStoreSelected] as any[]}
                  onClose={() => {
                    listStores();
                  }}
                  onSelect={value => {
                    handleSelectedFilters({ type: 'storesId', value });
                    field.onChange(value);
                    setParam('storesId', value.join(','));
                  }}
                  onClean={() => {
                    handleSelectedFilters({ type: 'storesId', value: [] });
                    field.onChange([]);
                    deleteParam('storesId');
                  }}
                  locale={{
                    noResultsText: isListStoresLoading ? 'Carregando lojas...' : 'Nenhum item encontrado',
                    searchPlaceholder: 'Pesquisar lojas',
                  }}
                  renderValue={(selected: any) => (
                    <span>
                      Lojas selecionadas <span className={styles.badge}>{selected.length}</span>
                    </span>
                  )}
                  renderMenuItem={(label, item: any) => {
                    const maxCategories = 2;
                    const firstTreeCategories = item?.categories?.filter((_category: string, index: number) => index < maxCategories);
                    const restCategories = item?.categories?.filter((_category: string, index: number) => index >= maxCategories);
                    const allCategories = item?.categories?.join(', ');

                    return allCategories ? (
                      <Whisper trigger="hover" placement="right" speaker={<Tooltip>{allCategories}</Tooltip>}>
                        <div className={styles['render-menu-item']}>
                          <span>{label}</span>
                          {firstTreeCategories?.map((category: string) => (
                            <span key={category} className={styles['highlight-optimized']}>
                              {category}
                            </span>
                          ))}
                          {restCategories?.length > 0 && (
                            <span className={styles['highlight-optimized']}>
                              + {restCategories.length} {restCategories.length > 1 ? 'OUTRAS' : 'OUTRA'}
                            </span>
                          )}
                        </div>
                      </Whisper>
                    ) : (
                      <span>{label}</span>
                    );
                  }}
                  renderExtraFooter={() => (
                    <div className={styles['render-extra-footer']}>
                      <Checkbox
                        block
                        indeterminate={indeterminateStores}
                        checked={isCheckedAllStores}
                        onChange={(_v, checked: boolean) => {
                          const nextValue = checked ? list.stores.map((item: { value: string }) => item.value) : [];

                          handleSelectedFilters({
                            type: 'storesId',
                            value: nextValue,
                          });
                          if (nextValue.length) {
                            setParam('storesId', nextValue.join(','));
                            field.onChange(nextValue);
                          } else {
                            deleteParam('storesId');
                          }
                        }}
                      >
                        Selecionar todas as lojas
                      </Checkbox>
                    </div>
                  )}
                  onSearch={search => {
                    debounce(() => {
                      dispatch(setSearch({ type: 'stores', value: search }));
                    });
                  }}
                />
                {showStoreFilterMessageError ? <p className={styles.error}>{showStoreFilterMessageError} </p> : null}
                {!showStoreFilterMessageError && showStoreFilterTip ? <p className={styles.tip}>{showStoreFilterTip} </p> : null}
              </>
            )}
          />
        </div>
        <div className={styles['filter-selection-wrapper']}>
          <span>Loja controle</span>
          <Controller
            control={control}
            name="filters.controlStoreId"
            render={({ field }) => (
              <>
                <SelectPicker
                  id="select-picker-pill"
                  style={{ maxWidth: '300px' }}
                  cleanable
                  searchable
                  placeholder={'Selecione a loja controle'}
                  type="select"
                  name="loja"
                  filter={false}
                  value={field.value}
                  data={controlStoreList}
                  onClose={() => {
                    listStores();
                  }}
                  onSelect={value => {
                    field.onChange(value);
                    setParam('controlStoreId', value);
                  }}
                  onClean={() => {
                    field.onChange('');
                    deleteParam('controlStoreId');
                  }}
                  locale={{
                    noResultsText: isListStoresLoading ? 'Carregando lojas...' : 'Nenhum item encontrado',
                    searchPlaceholder: 'Pesquisar lojas',
                  }}
                  onSearch={search => {
                    debounce(() => {
                      dispatch(setSearch({ type: 'stores', value: search }));
                    });
                  }}
                />
                {!field.value && <p className={styles.tip}>Opcional</p>}
              </>
            )}
          />
        </div>
      </div>
    </section>
  );
};
